import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

import { OrbiAgentWalletType } from '@patient-ui/patient-web/store';
import {
  ORBI_FALLBACK_TEXT,
  ORBI_RETURN_TYPE,
  isReturnType,
} from '@patient-ui/shared/constants';
import { OrbiMessageType } from '@patient-ui/shared/enums';
import {
  OrbiAccession,
  OrbiAppointment,
  OrbiFindLabData,
  OrbiMessage,
  OrbiPatientPassData,
} from '@patient-ui/shared/models';
import {
  OrbiInsuranceEligibilityMap,
  applyOrbiCheckedIn,
  applyOrbiInsuranceEligibility,
  mapOrbiAppointments,
  mapOrbiFindLabData,
  mapOrbiPatientPassData,
  orbiMessageLocksChat,
} from '@patient-ui/shared/utils';

import { OrbiAppointmentCardNavigate } from '../orbi-appointment-card/orbi-appointment-card.component';
import { OrbiFindABillFound } from '../orbi-find-a-bill-card/orbi-find-a-bill-card.component';
import { OrbiFindLabSearch } from '../orbi-find-lab-card/orbi-find-lab-card.component';
import { OrbiResultsCardNavigate } from '../orbi-results-card/orbi-results-card.component';

/**
 * Renders a single Orbi conversation turn (PCSA-13160 / PCSA-13174 / PCSA-13183).
 *
 * Presentational: distinguishes the user turn (right-aligned, navy bubble) from
 * an agent turn (left-aligned, white bubble with the asymmetric tail radius —
 * Figma node 7203:9988), plus the "thinking" loading state and a recoverable
 * error bubble.
 *
 * Agent-turn rendering is ROUTE-AWARE (PCSA-13183): a `text`-routed llm turn is
 * rendered as MARKDOWN via `<markdown>` (PCSA-13174, sanitized in
 * PortalOrbiModule) — the default path for Clinical/Support/Results-Data/
 * clarification answers, plus non-locking guardrails. A `fallback`-routed turn
 * (or a text turn with empty content) shows the approved fallback copy. The
 * `results-card` route renders `<patient-ui-orbi-results-card>` from the turn's
 * typed `structuredData.accessions` (PCSA-13224); its navigation intents are
 * re-emitted via `navigate` for the thread to route + close the panel.
 *
 * The `appointment-card` route fans out by RETURN TYPE (the parser routes all
 * five appointment types to one route, PCSA-13223):
 *  - `AppointmentDetails` → the Upcoming Appointment card set (PCSA-13397), from
 *    the flattened `structuredData.appointments`. Past rows split out into a
 *    Visit History card beneath it.
 *  - `VisitHistory` → the Visit History card, from the SAME flattened
 *    appointments (both are lists of appointment rows; mobile passes
 *    `UpcomingAppointmentData` to both cards too) but a different renderer: past
 *    visits nest inside one headed card and offer Rate Visit / See All rather
 *    than the upcoming card's check-in and insurance actions.
 *  - `AppointmentPatientPass` → the Labcorp Patient Pass card (PCSA-13374).
 *  - `AppointmentFindLab` → the Make an Appointment card (PCSA-13241).
 *  - `AppointmentCancelled` → no card; the agent's confirmation text carries the
 *    whole answer (mobile renders text only for that type too).
 * Each card's payload is mapped here, at the render boundary, so the parser stays
 * transport-shaped and the mapping is one pure function per card.
 *
 * The `billing-card` route likewise fans out by RETURN TYPE (the parser routes all
 * six billing types to one route, PCSA-13223):
 *  - `FindABill` → the Find a Bill card (PCSA-13419), whose lookup result is
 *    re-emitted via `findABillFound` for the thread to route + close the panel.
 *    `structuredData.invoiceNumbers[0]` prefills the invoice field when the agent
 *    sent one; an empty list is valid and leaves it blank.
 *  - the five NAVIGATION types (`PayABill`, `PaymentPlan`, `Invoices`,
 *    `InvoiceHistory`, `UpdateInsurance`) render no card BY DESIGN: their answer
 *    is the agent's text plus a hand-off to the portal page it names, which the
 *    thread owns (`OrbiBillingNavigationService`, PCSA-13416). So such a turn
 *    shows its accompanying agent text here — never a blank bubble, never raw
 *    JSON — and the user is taken to the page ~1.5s later.
 */
@Component({
  selector: 'patient-ui-orbi-message-item',
  templateUrl: './orbi-message-item.component.html',
  styleUrls: ['./orbi-message-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiMessageItemComponent {
  @Input() message!: OrbiMessage;

  @ViewChild('bubble') private bubble?: ElementRef<HTMLElement>;

  /**
   * The portal's own insurance-eligibility answers, keyed by confirmation number
   * (PCSA-13543). Supplied by the thread from the patient store; folded into the
   * mapped rows so the appointment card offers the same insurance action the
   * Appointments page does. Absent (or empty) leaves every row exactly as the
   * agent described it — see `applyOrbiInsuranceEligibility`.
   */
  @Input() insuranceEligibility?: OrbiInsuranceEligibilityMap;

  /**
   * Confirmation numbers this browser session has completed Mobile Check-in for
   * (PCSA-13577). Supplied by the thread from `MobileCheckInSessionService`; folded
   * into the mapped rows so the card draws the disabled "Checked In" pill — see
   * `applyOrbiCheckedIn`, which also explains why the check-in POST is the only
   * source available for this.
   */
  @Input() checkedInConfirmationNumbers?: readonly string[];

  /**
   * Emitted when the user activates a retry on a failed turn (PCSA-13172). The
   * host (thread) resends the failed request.
   *
   * Currently has NO trigger: the error bubble is text-only as of design review
   * 08/28, so nothing in the template calls `onRetry`. Kept, with the store slice
   * behind it (`retryLastMessage` + the reducer's `lastUserMessage` stash),
   * because the control has already been through link -> pill -> nothing and
   * bringing it back is one element in the template rather than a feature.
   */
  @Output() retry = new EventEmitter<void>();

  /**
   * A navigation intent from a rendered Results card (PCSA-13224) — "View
   * Result" / "View Details" (an accession) or "View All Results". Re-emitted
   * for the thread, which owns the router + panel-close (parity with mobile
   * `setOverlayOpen(false)` before navigating).
   */
  @Output() navigate = new EventEmitter<OrbiResultsCardNavigate>();

  /**
   * An action activated on an Upcoming Appointment card (PCSA-13397). Re-emitted
   * for the thread, which owns the router + panel-close.
   */
  @Output() appointmentNavigate =
    new EventEmitter<OrbiAppointmentCardNavigate>();

  /**
   * "See All" on an Upcoming Appointment card set (or the Visit History card split
   * from it). Re-emitted for the thread, which routes to the Appointments page and
   * closes the panel. Separate from `visitHistorySeeAll` because the destination
   * differs.
   */
  @Output() appointmentsSeeAll = new EventEmitter<void>();

  /**
   * A search submitted from a Make an Appointment card (PCSA-13241). Re-emitted
   * for the thread, which seeds the booking session and routes into the flow.
   */
  @Output() findLabSearch = new EventEmitter<OrbiFindLabSearch>();

  /**
   * "See All" on a Visit History card. Re-emitted for the thread, which routes to
   * the appointments history view and closes the panel (mobile's
   * `onNavigateToSubWidget(VisitHistoryScreen())`).
   */
  @Output() visitHistorySeeAll = new EventEmitter<void>();

  /**
   * "Rate Visit" on a Visit History card. The card itself carries the external
   * link, so this is a notification (analytics / panel state), not the navigation.
   */
  @Output() rateVisit = new EventEmitter<OrbiAppointment>();

  /**
   * A bill located from a Find a Bill card (PCSA-13419 AC 8). Re-emitted for the
   * thread, which owns the router + panel-close.
   */
  @Output() findABillFound = new EventEmitter<OrbiFindABillFound>();

  @Output() addToWallet = new EventEmitter<OrbiAgentWalletType>();

  /** Expose the enum to the template for type comparisons. */
  readonly OrbiMessageType = OrbiMessageType;

  /** Approved fallback copy for an empty / fallback-routed agent turn. */
  readonly fallbackText = ORBI_FALLBACK_TEXT;

  /**
   * Memoization for the appointment payload mappings: the `structuredData` object
   * the mapping was derived from, plus its result. Keyed on identity so a NEW turn
   * (or a replaced payload) re-maps while repeated reads within one render do not.
   */
  private appointmentsSource?: OrbiMessage['structuredData'];
  private appointmentsEligibilitySource?: OrbiInsuranceEligibilityMap;
  private appointmentsCheckedInSource?: readonly string[];
  private mappedAppointments: OrbiAppointment[] = [];
  private upcomingAppointmentsSource?: OrbiAppointment[];
  private mappedUpcomingAppointments: OrbiAppointment[] = [];
  private pastAppointmentsSource?: OrbiAppointment[];
  private mappedPastAppointments: OrbiAppointment[] = [];
  private patientPassSource?: OrbiMessage['structuredData'];
  private mappedPatientPass: OrbiPatientPassData = {};
  private findLabSource?: OrbiMessage['structuredData'];
  private mappedFindLab: OrbiFindLabData = {};

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  onRetry(): void {
    this.retry.emit();
  }

  /** Re-emit a Results-card navigation intent to the thread (PCSA-13224). */
  onCardNavigate(event: OrbiResultsCardNavigate): void {
    this.navigate.emit(event);
  }

  /**
   * True when this is a `results-card` turn that actually mapped accessions to
   * render (PCSA-13224). Guards the card so an empty/unmapped payload never
   * shows a blank card — that case already degraded to text/fallback upstream in
   * the parser, so it will fall through to `isFallbackTurn` here.
   */
  get isResultsCardTurn(): boolean {
    return (
      this.message.type === OrbiMessageType.Llm &&
      this.message.route === 'results-card' &&
      (this.message.structuredData?.accessions?.length ?? 0) > 0
    );
  }

  /** The accessions rendered by the Results card (empty when not a card turn). */
  get resultsAccessions(): OrbiAccession[] {
    return this.message.structuredData?.accessions ?? [];
  }

  // --- Appointment card routes (PCSA-13241 / 13374 / 13397) ------------------

  /**
   * True when this is an `appointment-card` turn of the given return type. All
   * five appointment types share one route (PCSA-13223), so the renderer fans out
   * on `returnType` — read from `structuredData` (the parser's echo of the
   * response) rather than the message's own copy so the payload and the branch can
   * never disagree.
   *
   * The comparison is case-INSENSITIVE (`isReturnType`), matching the router that
   * sent the turn here: the gateway's casing for `returnType` is not pinned, so
   * `isAppointmentReturnType` normalizes before deciding the route. A strict `===`
   * here would disagree with that decision — a `visithistory` payload would be
   * routed to `appointment-card` and then match none of the five branches, falling
   * through to the fallback bubble with the card silently missing.
   */
  private isAppointmentTurn(returnType: string): boolean {
    return (
      this.message.type === OrbiMessageType.Llm &&
      this.message.route === 'appointment-card' &&
      isReturnType(this.message.structuredData?.returnType, returnType)
    );
  }

  /**
   * True for an `AppointmentDetails` turn that mapped at least one renderable
   * appointment (PCSA-13397). A turn whose rows all failed to map falls through
   * to its accompanying agent text rather than showing an empty set.
   */
  get isAppointmentDetailsTurn(): boolean {
    return (
      (this.isAppointmentTurn(ORBI_RETURN_TYPE.AppointmentDetails) ||
        this.isMislabelledVisitHistory) &&
      this.appointments.length > 0
    );
  }

  /**
   * A `VisitHistory` turn mixing past and upcoming rows: some rows are
   * `past: true` and others are not. For "show all appointments" the agent
   * intermittently labels the whole answer VisitHistory (QA2 14 Oct: 2 `past:
   * true` + 5 `past: false`; local 14 Oct: 2 `past: true` + 1 row with no `past`
   * at all, "2 past visits and 1 upcoming"). Such a turn renders like
   * AppointmentDetails, using the same rule that split already uses — only
   * `past: true` is past — so upcoming appointments are never listed as visits.
   * A genuine visit-history answer (every row `past: true`, or no row flagged)
   * renders as before. Web-only safety net; the agent fix is tracked separately.
   */
  private get isMislabelledVisitHistory(): boolean {
    if (!this.isAppointmentTurn(ORBI_RETURN_TYPE.VisitHistory)) {
      return false;
    }
    const rows = this.message.structuredData?.appointments ?? [];
    const pastCount = rows.filter((row) => row['past'] === true).length;
    return pastCount > 0 && pastCount < rows.length;
  }

  get hasUpcomingAppointmentsCard(): boolean {
    return (
      this.isAppointmentDetailsTurn && this.upcomingAppointments.length > 0
    );
  }

  get hasPastAppointmentsCard(): boolean {
    return this.isAppointmentDetailsTurn && this.pastAppointments.length > 0;
  }

  /** Memoized for the same OnPush reason as `appointments`. */
  get upcomingAppointments(): OrbiAppointment[] {
    const all = this.appointments;
    if (this.upcomingAppointmentsSource !== all) {
      this.upcomingAppointmentsSource = all;
      this.mappedUpcomingAppointments = all.filter((a) => !a.isPast);
    }
    return this.mappedUpcomingAppointments;
  }

  get pastAppointments(): OrbiAppointment[] {
    const all = this.appointments;
    if (this.pastAppointmentsSource !== all) {
      this.pastAppointmentsSource = all;
      this.mappedPastAppointments = all.filter((a) => !!a.isPast);
    }
    return this.mappedPastAppointments;
  }

  /**
   * True for a `VisitHistory` turn that mapped at least one renderable visit.
   * Separate from `isAppointmentDetailsTurn` because past visits get their own
   * card, even though both return types carry the same appointment-row shape.
   */
  get isVisitHistoryTurn(): boolean {
    return (
      this.isAppointmentTurn(ORBI_RETURN_TYPE.VisitHistory) &&
      !this.isMislabelledVisitHistory &&
      this.appointments.length > 0
    );
  }

  /**
   * The mapped appointments for an AppointmentDetails/VisitHistory turn. Memoized
   * per message: the getter is read several times per change-detection pass (the
   * guard, the template binding) and the mapping is pure, so re-running it each
   * time would be wasted work — and a new array identity each call would defeat
   * the child card's OnPush input check.
   */
  get appointments(): OrbiAppointment[] {
    const structured = this.message.structuredData;
    const eligibility = this.insuranceEligibility;
    const checkedIn = this.checkedInConfirmationNumbers;
    if (
      this.appointmentsSource !== structured ||
      this.appointmentsEligibilitySource !== eligibility ||
      this.appointmentsCheckedInSource !== checkedIn
    ) {
      this.appointmentsSource = structured;
      this.appointmentsEligibilitySource = eligibility;
      this.appointmentsCheckedInSource = checkedIn;
      this.mappedAppointments = applyOrbiCheckedIn(
        applyOrbiInsuranceEligibility(
          mapOrbiAppointments(structured?.appointments),
          eligibility
        ),
        checkedIn
      );
    }
    return this.mappedAppointments;
  }

  /** True for an `AppointmentPatientPass` turn that carried an lpid (PCSA-13374). */
  get isPatientPassTurn(): boolean {
    return (
      this.isAppointmentTurn(ORBI_RETURN_TYPE.AppointmentPatientPass) &&
      !!this.patientPassData?.lpid
    );
  }

  /** The typed Patient Pass payload (memoized — see `appointments`). */
  get patientPassData(): OrbiPatientPassData {
    const structured = this.message.structuredData;
    if (this.patientPassSource !== structured) {
      this.patientPassSource = structured;
      this.mappedPatientPass = mapOrbiPatientPassData(structured?.raw);
    }
    return this.mappedPatientPass;
  }

  /**
   * True for an `AppointmentFindLab` turn (PCSA-13241). Unlike the other cards
   * this needs NO payload to render: every field is optional and `data: {}` is a
   * valid response (AC 3.2) — the card then shows an empty, editable form.
   */
  get isFindLabTurn(): boolean {
    return this.isAppointmentTurn(ORBI_RETURN_TYPE.AppointmentFindLab);
  }

  /** Text-only appointment turn; unlike the others it keeps feedback and copy. */
  get isAppointmentScheduleTurn(): boolean {
    return this.isAppointmentTurn(ORBI_RETURN_TYPE.AppointmentSchedule);
  }

  /** The typed Find a Lab prefill payload (memoized — see `appointments`). */
  get findLabData(): OrbiFindLabData {
    const structured = this.message.structuredData;
    if (this.findLabSource !== structured) {
      this.findLabSource = structured;
      this.mappedFindLab = mapOrbiFindLabData(structured?.raw);
    }
    return this.mappedFindLab;
  }

  /** True when ANY appointment card is rendered for this turn. */
  get isAppointmentCardTurn(): boolean {
    return (
      this.isAppointmentDetailsTurn ||
      this.isVisitHistoryTurn ||
      this.isPatientPassTurn ||
      this.isFindLabTurn
    );
  }

  /** Re-emit an appointment-card action to the thread (PCSA-13397). */
  onAppointmentNavigate(event: OrbiAppointmentCardNavigate): void {
    this.appointmentNavigate.emit(event);
  }

  /** Re-emit "See All" from an Upcoming Appointment card set to the thread. */
  onAppointmentsSeeAll(): void {
    this.appointmentsSeeAll.emit();
  }

  /** Re-emit a Find a Lab search to the thread (PCSA-13241). */
  onFindLabSearch(event: OrbiFindLabSearch): void {
    this.findLabSearch.emit(event);
  }

  /** Re-emit "See All" from a Visit History card to the thread. */
  onVisitHistorySeeAll(): void {
    this.visitHistorySeeAll.emit();
  }

  /** Re-emit "Rate Visit" from a Visit History card to the thread. */
  onRateVisit(visit: OrbiAppointment): void {
    this.rateVisit.emit(visit);
  }

  // --- Billing card route (PCSA-13419) --------------------------------------

  /**
   * True for a `FindABill` turn (PCSA-13419). Like the Find a Lab card this needs
   * NO payload to render: `data.invoices` may be empty (AC 4.2) and the card then
   * shows an empty, editable form. The comparison is case-insensitive for the same
   * reason `isAppointmentTurn`'s is — the router that sent the turn here tolerated
   * casing, so a strict `===` would drop the card on a `findabill` payload.
   */
  get isFindABillTurn(): boolean {
    return (
      this.message.type === OrbiMessageType.Llm &&
      this.message.route === 'billing-card' &&
      isReturnType(
        this.message.structuredData?.returnType,
        ORBI_RETURN_TYPE.FindABill
      )
    );
  }

  /**
   * Invoice numbers to prefill the Find a Bill card with (AC 4). Passed through
   * as the parser typed them — the card takes the first and trims it.
   */
  get invoiceNumbers(): string[] {
    return this.message.structuredData?.invoiceNumbers ?? [];
  }

  /** Re-emit a located bill from a Find a Bill card to the thread (AC 8). */
  onFindABillFound(event: OrbiFindABillFound): void {
    this.findABillFound.emit(event);
  }

  /**
   * True for an agent turn whose TEXT should render as markdown (PCSA-13183 /
   * PCSA-13242): the `text` route, a `guardrail` route (guardrail responses —
   * EMERGENCY/HARM/OFFTOPIC/FOREIGNLANGUAGE — render via the text path, showing
   * the agent's returned TextualResponse VERBATIM, AC 3/8), plus any turn with
   * text content. This deliberately also covers a card-routed turn that carries
   * `textualResponse` — 13183 AC 3 lists Results/Appointment/Billing *text*
   * responses as text-renderable. That deliberately INCLUDES the appointment-card
   * turns, which render the agent's text bubble ABOVE the card: mobile's
   * `_buildCardBubble` does exactly that for appointment payloads (text bubble,
   * 16px gap, then the card), and it is how a Cancelled turn or an unrenderable
   * appointment payload still shows its answer. `route` may be absent on
   * older/synthesized turns; missing route is treated as text.
   */
  get isTextTurn(): boolean {
    if (this.message.type !== OrbiMessageType.Llm) {
      return false;
    }
    // A rendered Results card OWNS its turn (PCSA-13224): the card carries the
    // content, so don't also render a text bubble above it from the accompanying
    // textualResponse (Figma shows the card alone). The appointment cards are the
    // opposite — see above.
    if (this.isResultsCardTurn) {
      return false;
    }
    return this.message.text.trim().length > 0;
  }

  /**
   * True when the agent turn has no renderable text — an explicit `fallback`
   * route (PCSA-13223), or ANY text-rendered turn (incl. a guardrail turn whose
   * TextualResponse is missing — PCSA-13242 AC 9) with empty content. Shows the
   * approved fallback copy instead of an empty bubble.
   *
   * An `appointment-card` or `billing-card` turn is treated as text-rendered when
   * NO card was built from it (e.g. a PatientPass with no lpid, rows that all
   * failed to map, or one of the five billing NAVIGATION types, which show text
   * and then hand off — PCSA-13416): with neither a card nor text there is nothing
   * to show, so the fallback copy applies rather than an empty bubble. A turn that
   * DID render a card never falls back, even with empty text — the card is the
   * content.
   */
  get isFallbackTurn(): boolean {
    if (this.message.type !== OrbiMessageType.Llm) {
      return false;
    }
    const route = this.message.route ?? 'text';
    if (route === 'fallback') {
      return true;
    }
    // A text/guardrail (or otherwise non-card) turn that arrived with no content.
    const isCard =
      route === 'results-card' ||
      (route === 'billing-card' && this.isFindABillTurn) ||
      (route === 'appointment-card' && this.isAppointmentCardTurn);
    return !isCard && this.message.text.trim().length === 0;
  }

  /** The turn's host element — lets the thread align this turn into view. */
  get hostElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  get isGuardrailLockTurn(): boolean {
    return (
      this.isTextTurn &&
      orbiMessageLocksChat(
        this.message.guardrailTriggered,
        this.message.guardrailType
      )
    );
  }

  focusGuidance(): boolean {
    const target = this.isGuardrailLockTurn
      ? this.bubble?.nativeElement
      : undefined;
    if (!target) {
      return false;
    }
    target.focus();
    return true;
  }

  /**
   * Fired by `<markdown>` after it renders (PCSA-13174). Make every rendered
   * link open in a NEW TAB with `rel="noopener noreferrer"` — parity with
   * mobile, which opens links externally (`UrlLauncherHelpers.openUrl`). Without
   * this the bare `<a href>` would navigate the whole portal SPA away from the
   * chat, losing the user's session + conversation.
   */
  onMarkdownReady(): void {
    const root = this.elementRef.nativeElement.querySelector<HTMLElement>(
      '.orbi-message__markdown'
    );
    if (!root) {
      return;
    }
    root
      .querySelectorAll<HTMLAnchorElement>('a[href]:not([href^="tel:"])')
      .forEach((a) => {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      });
    this.linkifyPhoneNumbers(root);
  }

  private static readonly PHONE_PATTERN =
    /(?:\+?1[-.\s])?(?:\(\d{3}\)\s?|\d{3}[-.\s])\d{3}[-.\s]\d{4}(?![-\d])/g;

  private static isNumberNeighbor(char: string | undefined): boolean {
    return char !== undefined && /[\d-]/.test(char);
  }

  private linkifyPhoneNumbers(root: HTMLElement): void {
    const doc = root.ownerDocument;
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const targets: Text[] = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = node as Text;
      if (!text.parentElement?.closest('a')) {
        targets.push(text);
      }
    }
    targets.forEach((text) => this.replacePhoneNumbers(text, doc));
  }

  private replacePhoneNumbers(text: Text, doc: Document): void {
    const value = text.data;
    const pattern = OrbiMessageItemComponent.PHONE_PATTERN;
    pattern.lastIndex = 0;
    const fragment = doc.createDocumentFragment();
    let cursor = 0;
    let match: RegExpExecArray | null = pattern.exec(value);

    while (match !== null) {
      const isSliceOfLongerNumber =
        OrbiMessageItemComponent.isNumberNeighbor(value[match.index - 1]) ||
        OrbiMessageItemComponent.isNumberNeighbor(value[pattern.lastIndex]);
      if (!isSliceOfLongerNumber) {
        fragment.append(value.slice(cursor, match.index));
        const link = doc.createElement('a');
        link.setAttribute('href', `tel:${match[0].replace(/[^+\d]/g, '')}`);
        link.textContent = match[0];
        fragment.append(link);
        cursor = pattern.lastIndex;
      }
      match = pattern.exec(value);
    }

    if (cursor === 0) {
      return;
    }
    fragment.append(value.slice(cursor));
    text.replaceWith(fragment);
  }
}
